<template>
  <aside v-if="file" class="fm-activity-panel">
    <div class="fm-activity-panel__header">
      <div class="fm-activity-panel__title">
        <span class="fm-activity-panel__title-icon">
          <v-icon small color="primary">mdi-history</v-icon>
        </span>
        <div class="fm-activity-panel__title-text">
          <span class="fm-activity-panel__name text-truncate">{{ file.name }}</span>
          <span class="fm-activity-panel__subtitle">Actividad del archivo</span>
        </div>
      </div>
      <div class="fm-activity-panel__actions">
        <v-btn
          icon
          small
          :loading="exportingPdf"
          :disabled="exportingPdf"
          aria-label="Descargar historial en PDF"
          title="Descargar historial en PDF"
          @click="downloadActivityPdf"
        >
          <v-icon small>mdi-file-pdf-box</v-icon>
        </v-btn>
        <v-btn icon small aria-label="Cerrar actividad" @click="$emit('close')">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="fm-activity-panel__body">
      <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

      <div v-else-if="loadingSummary" class="fm-activity-panel__state">
        <v-progress-circular indeterminate size="28" color="primary" />
      </div>

      <template v-else-if="!showDetail">
        <section class="fm-activity-hero">
          <div class="fm-activity-hero__main">
            <span class="fm-activity-hero__value">{{ summary.total || 0 }}</span>
            <span class="fm-activity-hero__label">
              {{ Number(summary.total) === 1 ? 'actividad registrada' : 'actividades registradas' }}
            </span>
          </div>
          <div v-if="summary.last_action" class="fm-activity-hero__last">
            <span class="fm-activity-hero__last-icon" :style="metaStyle(summary.last_action)">
              <v-icon x-small color="#fff">{{ actionMeta(summary.last_action).icon }}</v-icon>
            </span>
            <div class="fm-activity-hero__last-text">
              <span class="fm-activity-hero__last-action text-truncate">{{ actionLabel(summary.last_action) }}</span>
              <span v-if="summary.last_at" class="fm-activity-hero__last-date">{{ formatDate(summary.last_at) }}</span>
            </div>
          </div>
          <div v-else class="fm-activity-hero__none">Sin actividad todavía</div>
          <div v-if="summary.forge_status" class="fm-activity-hero__forge">
            <v-icon x-small color="primary">mdi-cube-outline</v-icon>
            <span>Forge: <strong>{{ summary.forge_status }}</strong></span>
          </div>
        </section>

        <section v-if="activityBreakdown.length" class="fm-activity-breakdown">
          <h3 class="fm-activity-breakdown__header">Actividad por tipo</h3>
          <div
            v-for="activity in activityBreakdown"
            :key="activity.action"
            class="fm-activity-breakdown__row"
          >
            <span class="fm-activity-breakdown__icon" :style="metaStyle(activity.action, true)">
              <v-icon x-small :color="actionMeta(activity.action).color">{{ actionMeta(activity.action).icon }}</v-icon>
            </span>
            <div class="fm-activity-breakdown__content">
              <div class="fm-activity-breakdown__line">
                <span class="fm-activity-breakdown__name text-truncate">{{ actionLabel(activity.action) }}</span>
                <strong>{{ activity.total }}</strong>
              </div>
              <div class="fm-activity-breakdown__bar">
                <span
                  :style="{ width: barWidth(activity.total), background: actionMeta(activity.action).color }"
                />
              </div>
            </div>
          </div>
        </section>

        <v-btn block depressed class="fm-activity-panel__cta" @click="openDetail">
          <v-icon left small>mdi-format-list-bulleted</v-icon>
          Ver detalle de actividad
        </v-btn>
      </template>

      <template v-else>
        <div class="fm-activity-panel__detail-header">
          <v-btn text small class="fm-activity-panel__back ml-n2" @click="showDetail = false">
            <v-icon left small>mdi-arrow-left</v-icon>
            Resumen
          </v-btn>
          <span class="fm-activity-panel__count">{{ detailTotal }} {{ detailTotal === 1 ? 'evento' : 'eventos' }}</span>
        </div>

        <div v-if="loadingDetail" class="fm-activity-panel__state">
          <v-progress-circular indeterminate size="28" color="primary" />
        </div>

        <div v-else-if="activities.length === 0" class="fm-activity-panel__empty">
          <span class="fm-activity-panel__empty-icon">
            <v-icon color="grey lighten-1">mdi-history</v-icon>
          </span>
          <span>No hay actividad registrada.</span>
        </div>

        <ol v-else class="fm-activity-timeline">
          <li v-for="activity in activities" :key="activity.id" class="fm-activity-item">
            <span class="fm-activity-item__dot" :style="metaStyle(activity.action)">
              <v-icon x-small color="#fff">{{ actionMeta(activity.action).icon }}</v-icon>
            </span>
            <div class="fm-activity-item__content">
              <div class="fm-activity-item__action">{{ actionLabel(activity.action) }}</div>
              <div class="fm-activity-item__meta">
                <v-icon x-small class="fm-activity-item__meta-icon">
                  {{ activity.user ? 'mdi-account-outline' : 'mdi-cog-outline' }}
                </v-icon>
                <span class="text-truncate">{{ activity.user?.name || 'Sistema' }}</span>
              </div>
              <div class="fm-activity-item__date">{{ formatDate(activity.created_at) }}</div>
            </div>
          </li>
        </ol>

        <v-pagination
          v-if="detailLastPage > 1"
          v-model="detailPage"
          :length="detailLastPage"
          total-visible="5"
          class="mt-3"
          @input="loadDetail"
        />
      </template>
    </div>
  </aside>
</template>

<script>
import { getFileActivity, getFileActivityPDF, getFileActivitySummary } from '@/helpers/api/fileManager'

export default {
  name: 'FMDetailsPanel',
  props: {
    file: { type: Object, default: null },
    projectId: { type: [Number, String], required: true }
  },
  data() {
    return {
      summary: { total: 0, counts: {}, last_action: null, last_at: null, forge_status: null },
      activityOrder: [
        'file_uploaded',
        'file_viewed',
        'file_forge_viewed',
        'file_download_requested',
        'file_zip_requested',
        'file_renamed',
        'file_moved',
        'file_deleted',
        'file_restored',
        'file_revision_created',
        'file_to_forge_requested',
        'file_forge_completed',
        'file_forge_failed',
        'associated_item_opened'
      ],
      activities: [],
      loadingSummary: false,
      loadingDetail: false,
      exportingPdf: false,
      error: null,
      showDetail: false,
      detailPage: 1,
      detailLastPage: 1,
      detailTotal: 0,
      requestToken: 0
    }
  },
  watch: {
    file: {
      immediate: true,
      handler() {
        this.showDetail = false
        this.detailPage = 1
        this.activities = []
        this.loadSummary()
      }
    }
  },
  computed: {
    activityBreakdown() {
      const counts = this.summary.counts || {}
      return Object.keys(counts)
        .filter(action => Number(counts[action]) > 0)
        .sort((left, right) => {
          const leftIndex = this.activityOrder.indexOf(left)
          const rightIndex = this.activityOrder.indexOf(right)
          if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right)
          if (leftIndex === -1) return 1
          if (rightIndex === -1) return -1
          return leftIndex - rightIndex
        })
        .map(action => ({ action, total: Number(counts[action]) }))
    },
    maxBreakdown() {
      return this.activityBreakdown.reduce((max, item) => Math.max(max, item.total), 1)
    }
  },
  methods: {
    async downloadActivityPdf() {
      if (!this.file?.id || this.exportingPdf) return
      this.exportingPdf = true
      this.error = null
      try {
        const response = await getFileActivityPDF({ file_id: this.file.id })
        if (!response?.pdf_url) throw new Error('No se recibió la URL del PDF.')
        window.open(response.pdf_url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        this.error = error?.response?.data?.message || error?.message || 'No fue posible generar el PDF.'
      } finally {
        this.exportingPdf = false
      }
    },
    async loadSummary() {
      if (!this.file?.id) return
      const token = ++this.requestToken
      this.loadingSummary = true
      this.error = null
      try {
        const response = await getFileActivitySummary({ file_id: this.file.id, project_id: this.projectId })
        if (token === this.requestToken) this.summary = response.summary || this.summary
      } catch (error) {
        if (token === this.requestToken) this.error = error?.response?.data?.message || 'No fue posible cargar la actividad.'
      } finally {
        if (token === this.requestToken) this.loadingSummary = false
      }
    },
    openDetail() {
      this.showDetail = true
      this.detailPage = 1
      this.loadDetail()
    },
    async loadDetail() {
      if (!this.file?.id) return
      const token = ++this.requestToken
      this.loadingDetail = true
      this.error = null
      try {
        const response = await getFileActivity({ file_id: this.file.id, page: this.detailPage, per_page: 10 })
        if (token !== this.requestToken) return
        this.activities = response.activities || []
        this.detailLastPage = response.last_page || 1
        this.detailTotal = response.total || 0
      } catch (error) {
        if (token === this.requestToken) this.error = error?.response?.data?.message || 'No fue posible cargar el detalle.'
      } finally {
        if (token === this.requestToken) this.loadingDetail = false
      }
    },
    actionLabel(action) {
      return {
        file_uploaded: 'Archivo subido',
        file_viewed: 'Archivo visualizado',
        file_forge_viewed: 'Archivo visualizado en Forge',
        file_download_requested: 'Descarga solicitada',
        file_zip_requested: 'ZIP solicitado',
        file_renamed: 'Archivo renombrado',
        file_moved: 'Archivo movido',
        file_deleted: 'Archivo eliminado',
        file_restored: 'Archivo restaurado',
        file_revision_created: 'Revisión creada',
        file_to_forge_requested: 'Enviado a Forge',
        file_forge_completed: 'Forge completado',
        file_forge_failed: 'Forge fallido',
        associated_item_opened: 'Elemento asociado abierto'
      }[action] || action
    },
    actionMeta(action) {
      const neutral = { icon: 'mdi-circle-small', color: '#6B7280' }
      return {
        file_uploaded: { icon: 'mdi-upload', color: '#2D4B95' },
        file_viewed: { icon: 'mdi-eye-outline', color: '#6B7280' },
        file_forge_viewed: { icon: 'mdi-cube-scan', color: '#6B7280' },
        file_download_requested: { icon: 'mdi-download', color: '#0E7C86' },
        file_zip_requested: { icon: 'mdi-folder-zip-outline', color: '#0E7C86' },
        file_renamed: { icon: 'mdi-pencil-outline', color: '#B7791F' },
        file_moved: { icon: 'mdi-folder-move-outline', color: '#B7791F' },
        file_deleted: { icon: 'mdi-trash-can-outline', color: '#C53030' },
        file_restored: { icon: 'mdi-restore', color: '#2F855A' },
        file_revision_created: { icon: 'mdi-source-branch', color: '#6B46C1' },
        file_to_forge_requested: { icon: 'mdi-cube-send', color: '#2D4B95' },
        file_forge_completed: { icon: 'mdi-check-circle-outline', color: '#2F855A' },
        file_forge_failed: { icon: 'mdi-alert-circle-outline', color: '#C53030' },
        associated_item_opened: { icon: 'mdi-link-variant', color: '#6B46C1' }
      }[action] || neutral
    },
    metaStyle(action, soft = false) {
      const color = this.actionMeta(action).color
      return soft ? { background: color + '1A' } : { background: color }
    },
    barWidth(total) {
      return Math.max(6, Math.round((total / this.maxBreakdown) * 100)) + '%'
    },
    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString('es-CL', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.fm-activity-panel {
  width: 320px;
  min-width: 320px;
  height: 100%;
  background: var(--fm-surface);
  border-left: 1px solid var(--fm-border2);
  box-shadow: var(--fm-shadow);
  display: flex;
  flex-direction: column;
  z-index: 3;
}

/* Encabezado */
.fm-activity-panel__header {
  min-height: 60px;
  padding: 10px 12px 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid var(--fm-border);
}
.fm-activity-panel__title {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.fm-activity-panel__actions {
  display: flex;
  align-items: center;
  gap: 5px;
}
.fm-activity-panel__title-icon {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(45, 75, 149, 0.1);
}
.fm-activity-panel__title-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.fm-activity-panel__name {
  font-size: 13px;
  font-weight: 600;
}
.fm-activity-panel__subtitle {
  color: var(--fm-text2);
  font-size: 11.5px;
}

.fm-activity-panel__body {
  padding: 16px;
  overflow-y: auto;
  flex: 1;
}
.fm-activity-panel__state {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Resumen */
.fm-activity-hero {
  background: var(--fm-surface2);
  border: 1px solid var(--fm-border);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}
.fm-activity-hero__main {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.fm-activity-hero__value {
  font-size: 34px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: -0.02em;
}
.fm-activity-hero__label {
  color: var(--fm-text2);
  font-size: 12.5px;
}
.fm-activity-hero__last {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid var(--fm-border);
}
.fm-activity-hero__last-icon {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.fm-activity-hero__last-text {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.3;
}
.fm-activity-hero__last-action {
  font-size: 13px;
  font-weight: 600;
}
.fm-activity-hero__last-date {
  color: var(--fm-text2);
  font-size: 11.5px;
}
.fm-activity-hero__none {
  margin-top: 12px;
  color: var(--fm-text2);
  font-size: 12.5px;
}
.fm-activity-hero__forge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 12px;
  padding: 3px 9px;
  border-radius: 999px;
  background: rgba(45, 75, 149, 0.1);
  font-size: 11.5px;
}

/* Desglose */
.fm-activity-breakdown {
  margin-bottom: 20px;
}
.fm-activity-breakdown__header {
  margin: 0 0 8px;
  color: var(--fm-text2);
  font-size: 12px;
  font-weight: 600;
}
.fm-activity-breakdown__row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
}
.fm-activity-breakdown__icon {
  flex: 0 0 26px;
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
}
.fm-activity-breakdown__content {
  flex: 1;
  min-width: 0;
}
.fm-activity-breakdown__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12.5px;
}
.fm-activity-breakdown__bar {
  height: 3px;
  margin-top: 5px;
  border-radius: 2px;
  background: var(--fm-border);
  overflow: hidden;
}
.fm-activity-breakdown__bar span {
  display: block;
  height: 100%;
  border-radius: 2px;
  opacity: 0.75;
}

.fm-activity-panel__cta {
  border-radius: 8px;
  text-transform: none;
  letter-spacing: 0;
  background-color: var(--fm-accent) !important;
  color: #fff !important;
}
.fm-activity-panel__back {
  color: var(--fm-accent) !important;
}

/* Detalle */
.fm-activity-panel__detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.fm-activity-panel__count {
  color: var(--fm-text2);
  font-size: 12px;
}
.fm-activity-panel__empty {
  min-height: 180px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: var(--fm-text2);
  font-size: 13px;
  text-align: center;
}
.fm-activity-panel__empty-icon {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--fm-surface2);
}

.fm-activity-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}
.fm-activity-item {
  position: relative;
  display: flex;
  gap: 12px;
  padding-bottom: 16px;
}
.fm-activity-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 11px;
  top: 26px;
  bottom: 2px;
  width: 2px;
  background: var(--fm-border);
}
.fm-activity-item__dot {
  flex: 0 0 24px;
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  z-index: 1;
}
.fm-activity-item__content {
  min-width: 0;
  flex: 1;
  line-height: 1.35;
}
.fm-activity-item__action {
  font-size: 13px;
  font-weight: 600;
}
.fm-activity-item__meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  color: var(--fm-text2);
  font-size: 12px;
}
.fm-activity-item__meta-icon {
  color: var(--fm-text2) !important;
}
.fm-activity-item__source {
  flex: 0 0 auto;
  margin-left: 4px;
  padding: 0 6px;
  border-radius: 4px;
  background: var(--fm-surface2);
  border: 1px solid var(--fm-border);
  font-size: 10.5px;
}
.fm-activity-item__date {
  margin-top: 2px;
  color: var(--fm-text2);
  font-size: 11.5px;
}

@media (max-width: 700px) {
  .fm-activity-panel {
    width: 100%;
    min-width: 0;
    position: absolute;
    inset: 0;
  }
}
</style>